/**
 * NewCustomerWizard — the manual "Neuer Kunde" flow (feat/create-customer-and-attachment).
 *
 * Step 1: choose Natürliche Person vs Juristische Person (two cards) → Weiter.
 * Step 2: the matching form (person fields vs firma fields) → "Kunde erstellen"
 *         → POST /customers → onCreated(subjectType, subjectId).
 *
 * Reuses the admin Dialog / TextInput / Button primitives + the useCreateCustomer
 * mutation (typed api-client). The parent (CustomersScreen) owns navigation + toast.
 *
 * [Source: apps/web/src/features/administration/components.tsx] (Dialog / TextInput / Button)
 * [Source: apps/web/src/features/mailbox/components/DisconnectDialog.tsx] (Dialog shell usage)
 */

import { useState } from 'react';
import { User, Building2, ArrowLeft } from 'lucide-react';
import { Dialog, TextInput, Button } from '../../administration/components';
import { FirmNameAutocomplete } from '../../../components/ui/FirmNameAutocomplete';
import { cn } from '../../../lib/cn';
import type { CreateCustomerBody } from '../../../lib/api-client';
import type { CustomerKundentyp } from '@brokly/shared';
import { useCreateCustomer } from '../hooks/useCreateCustomer';

type Step = 'type' | 'form';

/** Field label + control wrapper (stacked). */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium">{label}</span>
      {children}
    </label>
  );
}

/** A native select styled to match TextInput (6px radius, 36px height). */
function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={cn(
        'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring',
        props.className,
      )}
    />
  );
}

/** One of the two type-choice cards on step 1. */
function TypeCard({
  active,
  icon,
  title,
  hint,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  title: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex flex-1 flex-col items-start gap-2 rounded-xl border p-4 text-left transition-colors',
        active
          ? 'border-primary bg-primary/5 ring-1 ring-primary'
          : 'border-border bg-card hover:border-primary/50',
      )}
    >
      <span
        className={cn(
          'grid size-9 place-items-center rounded-lg',
          active ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground',
        )}
      >
        {icon}
      </span>
      <span className="text-sm font-semibold">{title}</span>
      <span className="text-xs text-muted-foreground">{hint}</span>
    </button>
  );
}

const EMPTY_FORM: CreateCustomerBody = { kundentyp: 'natuerliche_person' };

export function NewCustomerWizard({
  open,
  onClose,
  onCreated,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  /** Called after a successful create — parent navigates to the detail. */
  onCreated: (subjectType: 'person' | 'firma', subjectId: string) => void;
  /** Optional error toast hook. */
  onError?: (message: string) => void;
}) {
  const [step, setStep] = useState<Step>('type');
  const [kundentyp, setKundentyp] = useState<CustomerKundentyp>('natuerliche_person');
  const [form, setForm] = useState<CreateCustomerBody>(EMPTY_FORM);
  const mutation = useCreateCustomer();

  function reset() {
    setStep('type');
    setKundentyp('natuerliche_person');
    setForm(EMPTY_FORM);
    mutation.reset();
  }

  function close() {
    reset();
    onClose();
  }

  function set<K extends keyof CreateCustomerBody>(key: K, value: CreateCustomerBody[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function goToForm() {
    setForm({ ...EMPTY_FORM, kundentyp });
    setStep('form');
  }

  const isPerson = kundentyp === 'natuerliche_person';

  // Required-field gate (mirrors the api DTO): person → last_name; firma → name.
  const canSubmit = isPerson
    ? !!form.last_name?.trim()
    : !!form.name?.trim();

  function submit() {
    if (!canSubmit || mutation.isPending) return;

    // Trim + drop empty optionals so the api validators (IsEmail etc.) don't see ''.
    const body: CreateCustomerBody = { kundentyp };
    const put = <K extends keyof CreateCustomerBody>(k: K, v: string | undefined) => {
      const t = v?.trim();
      if (t) (body[k] as string) = t;
    };
    if (isPerson) {
      put('salutation', form.salutation);
      put('first_name', form.first_name);
      put('last_name', form.last_name);
      put('date_of_birth', form.date_of_birth);
      if (form.correspondence_language) body.correspondence_language = form.correspondence_language;
    } else {
      put('name', form.name);
      put('uid', form.uid);
      put('domain', form.domain);
      put('address', form.address);
    }
    put('email', form.email);
    put('phone', form.phone);

    mutation.mutate(body, {
      onSuccess: (res) => {
        onCreated(res.subjectType, res.subjectId);
        reset();
      },
      onError: (err) => {
        onError?.(err.message || 'Kunde konnte nicht erstellt werden.');
      },
    });
  }

  const footer =
    step === 'type' ? (
      <>
        <Button variant="ghost" onClick={close}>
          Abbrechen
        </Button>
        <Button onClick={goToForm}>Weiter</Button>
      </>
    ) : (
      <>
        <Button variant="ghost" onClick={() => setStep('type')} disabled={mutation.isPending}>
          <ArrowLeft className="size-3.5" /> Zurück
        </Button>
        <Button onClick={submit} disabled={!canSubmit || mutation.isPending}>
          {mutation.isPending ? 'Wird erstellt …' : 'Kunde erstellen'}
        </Button>
      </>
    );

  return (
    <Dialog
      open={open}
      onClose={close}
      title="Neuer Kunde"
      description={
        step === 'type'
          ? 'Wählen Sie den Kundentyp.'
          : isPerson
            ? 'Natürliche Person: Angaben erfassen.'
            : 'Juristische Person: Angaben erfassen.'
      }
      footer={footer}
      wide
    >
      {step === 'type' ? (
        <div className="flex gap-3">
          <TypeCard
            active={isPerson}
            icon={<User className="size-5" />}
            title="Natürliche Person"
            hint="Privatkunde (Einzelperson)"
            onClick={() => setKundentyp('natuerliche_person')}
          />
          <TypeCard
            active={!isPerson}
            icon={<Building2 className="size-5" />}
            title="Juristische Person"
            hint="Firma / Unternehmen"
            onClick={() => setKundentyp('juristische_person')}
          />
        </div>
      ) : isPerson ? (
        <div className="grid grid-cols-2 gap-4">
          <Field label="Anrede">
            <Select
              value={form.salutation ?? ''}
              onChange={(e) => set('salutation', e.target.value || undefined)}
            >
              <option value="">-</option>
              <option value="Herr">Herr</option>
              <option value="Frau">Frau</option>
            </Select>
          </Field>
          <Field label="Korrespondenzsprache">
            <Select
              value={form.correspondence_language ?? ''}
              onChange={(e) =>
                set(
                  'correspondence_language',
                  (e.target.value || undefined) as CreateCustomerBody['correspondence_language'],
                )
              }
            >
              <option value="">-</option>
              <option value="de">Deutsch</option>
              <option value="fr">Französisch</option>
              <option value="it">Italienisch</option>
              <option value="en">Englisch</option>
            </Select>
          </Field>
          <Field label="Vorname">
            <TextInput
              value={form.first_name ?? ''}
              onChange={(e) => set('first_name', e.target.value)}
              placeholder="Hans"
            />
          </Field>
          <Field label="Nachname *">
            <TextInput
              value={form.last_name ?? ''}
              onChange={(e) => set('last_name', e.target.value)}
              placeholder="Muster"
            />
          </Field>
          <Field label="Geburtsdatum">
            <TextInput
              type="date"
              value={form.date_of_birth ?? ''}
              onChange={(e) => set('date_of_birth', e.target.value)}
            />
          </Field>
          <div />
          <Field label="E-Mail">
            <TextInput
              type="email"
              value={form.email ?? ''}
              onChange={(e) => set('email', e.target.value)}
              placeholder="hans.muster@example.ch"
            />
          </Field>
          <Field label="Telefon">
            <TextInput
              value={form.phone ?? ''}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="+41 …"
            />
          </Field>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Field label="Firmenname *">
              <FirmNameAutocomplete
                value={form.name ?? ''}
                onChange={(name) => set('name', name)}
                onPick={(row) => {
                  set('name', row.name);
                  if (row.uid) set('uid', row.uid);
                }}
                placeholder="Basler Chemie AG"
              />
            </Field>
          </div>
          <Field label="UID">
            <TextInput
              value={form.uid ?? ''}
              onChange={(e) => set('uid', e.target.value)}
              placeholder="CHE-123.456.789"
            />
          </Field>
          <Field label="Domain">
            <TextInput
              value={form.domain ?? ''}
              onChange={(e) => set('domain', e.target.value)}
              placeholder="basler-chemie.ch"
            />
          </Field>
          <div className="col-span-2">
            <Field label="Adresse">
              <TextInput
                value={form.address ?? ''}
                onChange={(e) => set('address', e.target.value)}
                placeholder="Strasse Nr., PLZ Ort"
              />
            </Field>
          </div>
          <Field label="E-Mail">
            <TextInput
              type="email"
              value={form.email ?? ''}
              onChange={(e) => set('email', e.target.value)}
              placeholder="info@basler-chemie.ch"
            />
          </Field>
          <Field label="Telefon">
            <TextInput
              value={form.phone ?? ''}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="+41 …"
            />
          </Field>
        </div>
      )}

      {mutation.isError && (
        <p className="mt-4 text-sm text-status-danger" role="alert">
          {mutation.error?.message || 'Kunde konnte nicht erstellt werden.'}
        </p>
      )}
    </Dialog>
  );
}
